/**
 * LocationPreview , collapsible Pin Map + Street View for the Prospect Intake form.
 *
 * Lifecycle:
 *  1. Parent passes `address`, `city`, `state`, `zip_code`. We watch those
 *     props and after an 800ms debounce (or on blur via parent) we geocode
 *     and drop a pin.
 *  2. User can drag the pin to refine. We send {lat, lng} up via onCoordsChange.
 *  3. Street View panel auto-loads the same coordinates; if there's no
 *     panoramic imagery within 50m, we show a clean placeholder.
 */
import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, AdvancedMarker, ControlPosition, MapControl, useMap } from "@vis.gl/react-google-maps";
import { ChevronDown, ChevronRight, Layers, MapPin, AlertCircle, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { GMAPS_KEY, GMAPS_MAP_ID, GMAPS_LIBS, isMapsKeyConfigured, geocodeAddress } from "./gmaps";

const DEBOUNCE_MS = 800;

export default function LocationPreview({
  address, city, state, zip_code,
  initialLat, initialLng,
  onCoordsChange,
  onCountyResolved,
}) {
  const isDesktop = typeof window !== "undefined" && window.matchMedia?.("(min-width: 768px)").matches;
  const [open, setOpen] = useState(!!isDesktop);
  const [coords, setCoords] = useState(
    Number.isFinite(initialLat) && Number.isFinite(initialLng)
      ? { lat: initialLat, lng: initialLng }
      : null,
  );
  const [geoErr, setGeoErr] = useState(null);
  const [mapType, setMapType] = useState("roadmap"); // roadmap | hybrid
  const debounceRef = useRef(null);
  const lastSeedRef = useRef(""); // last input that we successfully geocoded (skip dupes)

  // Debounced auto-geocode when address inputs change.
  useEffect(() => {
    if (!isMapsKeyConfigured()) return;
    const a = (address || "").trim();
    if (a.length < 5) return; // require at least a street name
    const key = [a, city, state, zip_code].join("|");
    if (key === lastSeedRef.current) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await geocodeAddress({ address: a, city, state, zip_code });
        lastSeedRef.current = key;
        setCoords({ lat: r.lat, lng: r.lng });
        setGeoErr(null);
        onCoordsChange?.({ lat: r.lat, lng: r.lng });
        if (r.county) onCountyResolved?.(r.county);
      } catch (e) {
        setGeoErr(e.message || "Could not geocode address");
        // Non-blocking toast , only show once per failure key
        toast.message("Address could not be geocoded. Map unavailable.");
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [address, city, state, zip_code, onCoordsChange]);

  const handleDrag = (latLng) => {
    setCoords(latLng);
    onCoordsChange?.(latLng);
  };

  if (!isMapsKeyConfigured()) {
    return (
      <CollapsibleHeader open={open} onToggle={() => setOpen((o) => !o)} title="Location Preview">
        <div className="ae-card p-4 text-xs text-amber-300 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> Google Maps key not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY in frontend/.env.
        </div>
      </CollapsibleHeader>
    );
  }

  return (
    <CollapsibleHeader open={open} onToggle={() => setOpen((o) => !o)} title="Location Preview">
      {open && (
        <APIProvider apiKey={GMAPS_KEY} libraries={GMAPS_LIBS}>
          <div className="space-y-2" data-testid="location-preview">
            <PinMap
              height={280}
              coords={coords}
              mapType={mapType}
              onMapType={setMapType}
              onPinDrag={handleDrag}
              gestureHandling="cooperative"
              placeholderMessage={
                geoErr
                  ? "Address could not be geocoded. Type a valid street, city, and state."
                  : "Type a full address (street, city, state) to see the map."
              }
            />
            <StreetViewPanel height={220} coords={coords} />
          </div>
        </APIProvider>
      )}
    </CollapsibleHeader>
  );
}

// =====================================================================
// PinMap , interactive map with a draggable pin
// =====================================================================

export function PinMap({
  height = 280,
  coords,
  mapType = "roadmap",
  onMapType,
  onPinDrag,
  gestureHandling = "cooperative",
  showFullscreen = false,
  placeholderMessage = "",
  pins = [], // extra pins (for comparables overlay)
  circle = null, // {center: {lat,lng}, radiusMeters: number}
  defaultZoom = 16,
  onMapReady,
  testid = "pin-map",
}) {
  if (!coords) {
    return (
      <div
        className="ae-card flex items-center justify-center text-xs text-slate-500 px-4"
        style={{ height }}
        data-testid={`${testid}-empty`}
      >
        <div className="text-center">
          <MapPin className="h-6 w-6 mx-auto mb-2 text-slate-600" />
          {placeholderMessage || "Awaiting coordinates"}
        </div>
      </div>
    );
  }
  return (
    <div className="relative ae-card overflow-hidden" style={{ height }} data-testid={testid}>
      <Map
        key={`${coords.lat.toFixed(3)},${coords.lng.toFixed(3)}`}
        defaultCenter={coords}
        defaultZoom={defaultZoom}
        mapId={GMAPS_MAP_ID}
        gestureHandling={gestureHandling}
        disableDefaultUI={false}
        mapTypeId={mapType}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={showFullscreen}
        style={{ width: "100%", height: "100%" }}
      >
        <AdvancedMarker
          position={coords}
          draggable={!!onPinDrag}
          onDragEnd={(e) => {
            const ll = e.latLng;
            if (ll && onPinDrag) onPinDrag({ lat: ll.lat(), lng: ll.lng() });
          }}
        />
        {pins.map((p) => (
          <AdvancedMarker key={p.key || `${p.lat},${p.lng}`} position={{ lat: p.lat, lng: p.lng }} title={p.title}>
            <div className="h-3 w-3 rounded-full bg-emerald-400 border-2 border-white shadow" />
          </AdvancedMarker>
        ))}
        {circle && <CircleOverlay center={circle.center} radiusMeters={circle.radiusMeters} />}
        <ReadyBridge onReady={onMapReady} />
      </Map>
      {/* Hybrid/Roadmap toggle */}
      {onMapType && (
        <button
          type="button"
          onClick={() => onMapType(mapType === "roadmap" ? "hybrid" : "roadmap")}
          data-testid={`${testid}-type-toggle`}
          className="absolute top-2 right-2 z-10 bg-white/95 text-slate-900 text-[11px] font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm shadow hover:bg-white"
        >
          <Layers className="h-3 w-3" />
          {mapType === "roadmap" ? "Satellite" : "Roadmap"}
        </button>
      )}
    </div>
  );
}

function ReadyBridge({ onReady }) {
  const map = useMap();
  useEffect(() => { if (map && onReady) onReady(map); }, [map, onReady]);
  return null;
}

function CircleOverlay({ center, radiusMeters }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps?.Circle) return;
    const c = new window.google.maps.Circle({
      map,
      center,
      radius: radiusMeters,
      strokeColor: "#10B981",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#10B981",
      fillOpacity: 0.1,
    });
    return () => c.setMap(null);
  }, [map, center, radiusMeters]);
  return null;
}

// =====================================================================
// StreetViewPanel , read-only static panorama
// =====================================================================

export function StreetViewPanel({ height = 220, coords, pegmanEnabled = false, testid = "street-view" }) {
  const containerRef = useRef(null);
  const panoRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!coords || !containerRef.current || !window.google?.maps?.StreetViewService) return;
    const svc = new window.google.maps.StreetViewService();
    setUnavailable(false);
    setLoaded(false);
    svc.getPanorama({ location: coords, radius: 50, source: window.google.maps.StreetViewSource.OUTDOOR }, (data, status) => {
      if (status !== "OK" || !data?.location?.latLng) {
        setUnavailable(true);
        return;
      }
      panoRef.current = new window.google.maps.StreetViewPanorama(containerRef.current, {
        position: data.location.latLng,
        pov: { heading: 0, pitch: 0 },
        zoom: 0,
        addressControl: false,
        linksControl: pegmanEnabled,
        panControl: false,
        enableCloseButton: false,
        fullscreenControl: false,
        motionTracking: false,
        zoomControl: pegmanEnabled,
        showRoadLabels: false,
      });
      // Aim the panorama at the actual pin (heading from pano to pin).
      try {
        const heading = window.google.maps.geometry?.spherical?.computeHeading(
          data.location.latLng,
          new window.google.maps.LatLng(coords.lat, coords.lng),
        );
        if (Number.isFinite(heading)) panoRef.current.setPov({ heading, pitch: 0 });
      } catch { /* geometry library not loaded , heading defaults to 0 */ }
      setLoaded(true);
    });
  }, [coords, pegmanEnabled]);

  if (!coords) {
    return (
      <div className="ae-card flex items-center justify-center text-xs text-slate-500" style={{ height }} data-testid={`${testid}-empty`}>
        Awaiting coordinates
      </div>
    );
  }
  if (unavailable) {
    return (
      <div
        className="ae-card flex items-center justify-center text-xs text-slate-400 bg-slate-800/40"
        style={{ height, backgroundImage: "linear-gradient(135deg,#1F2937 0%,#374151 100%)" }}
        data-testid={`${testid}-unavailable`}
      >
        <div className="text-center">
          <EyeOff className="h-6 w-6 mx-auto mb-2 text-slate-500" />
          Street View not available for this address
        </div>
      </div>
    );
  }
  return (
    <div className="ae-card overflow-hidden relative" style={{ height }} data-testid={testid}>
      <div ref={containerRef} className="w-full h-full" />
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 bg-slate-900/60">
          Loading Street View…
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Collapsible wrapper
// =====================================================================

function CollapsibleHeader({ open, onToggle, title, children }) {
  return (
    <div className="space-y-2" data-testid="location-preview-section">
      <button
        type="button"
        onClick={onToggle}
        data-testid="location-preview-toggle"
        className="w-full flex items-center justify-between text-left px-3 py-2 rounded-sm border border-white/10 hover:border-emerald-500/30 transition group"
      >
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 group-hover:text-emerald-300">
          <MapPin className="h-3.5 w-3.5 text-emerald-400" />
          {title}
        </div>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
      </button>
      {open && children}
    </div>
  );
}
